import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Inline image carousel for room cards.
 * Shows ONE image at a time from `images[]` (the photos uploaded for that
 * specific room) with left/right arrows and clickable dots. Stays inside
 * the card; no fullscreen overlay.
 *
 * Props:
 *   - images: string[] — photos for the current room only
 *   - alt: string — accessible alt label
 *   - aspect: tailwind aspect class, default "aspect-[4/3]"
 *   - badge?: ReactNode — element rendered top-right (e.g. price pill)
 *   - className?: string — extra container classes
 *   - testIdSlug?: string — used to scope data-testid hooks
 */
export default function RoomImageCarousel({
  images = [],
  alt = "",
  aspect = "aspect-[4/3]",
  badge = null,
  className = "",
  testIdSlug = "",
}) {
  const [idx, setIdx] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const safeImages = images.filter(Boolean);
  const count = safeImages.length;
  const hasMany = count > 1;

  const prev = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i - 1 + count) % count);
  };
  const next = (e) => {
    e?.stopPropagation();
    setIdx((i) => (i + 1) % count);
  };

  return (
    <div
      className={`relative overflow-hidden ${aspect} bg-[#FDFBF7] ${className}`}
      onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchStartX == null || !hasMany) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (dx > 40) prev();
        else if (dx < -40) next();
        setTouchStartX(null);
      }}
      data-testid={testIdSlug ? `room-carousel-${testIdSlug}` : undefined}
    >
      {count > 0 ? (
        <img
          src={safeImages[idx]}
          alt={`${alt} — foto ${idx + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full bg-[#E8E2D9]" />
      )}

      {badge}

      {hasMany && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Foto precedente"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 hover:bg-black/65 text-white flex items-center justify-center transition opacity-80 hover:opacity-100"
            data-testid={testIdSlug ? `carousel-prev-${testIdSlug}` : undefined}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Foto successiva"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 hover:bg-black/65 text-white flex items-center justify-center transition opacity-80 hover:opacity-100"
            data-testid={testIdSlug ? `carousel-next-${testIdSlug}` : undefined}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {safeImages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                aria-label={`Vai alla foto ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
